import React, { FC } from 'react';
import { twMerge } from 'tailwind-merge';
import classnames from 'classnames';
import { useAppSelector } from 'hooks';
import {
  BinIcon,
  EditIconBlue,
  ExpandImageIcon,
  InvoiceSettingsIcon,
  JobTimelineNoteIcon,
  VerticalListActionsIcon
} from 'assets/icons';
import { CustomPopover } from 'components';
import { selectUserDetails } from 'containers/app/appSlice';

import { JobTimelineEvent, JobTimelineEventType } from '../jobs.model';

interface JobTimelineProps {
  jobTimeline: JobTimelineEvent[];
  limit?: number;
  onEditNoteClick: (event: JobTimelineEvent) => void;
  onDeleteNoteClick: (event: JobTimelineEvent) => void;
  hasImageViewer?: boolean;
  showPreview?: (index: number, event: JobTimelineEvent) => void;
  timelineId: string;
  currentActionsPopoverId: number;
  setCurrentActionsPopoverId: (noteId: number) => void;
  subTitleClassName?: string;
  titleClassName?: string;
  subTextClassName?: string;
  timelineClassName?: string;
}

const JobTimeline: FC<JobTimelineProps> = ({
  jobTimeline,
  limit,
  onEditNoteClick,
  onDeleteNoteClick,
  hasImageViewer,
  showPreview,
  timelineId,
  currentActionsPopoverId,
  setCurrentActionsPopoverId,
  subTitleClassName,
  titleClassName,
  subTextClassName,
  timelineClassName
}) => {
  const currentUser = useAppSelector(selectUserDetails);
  const getTimelineIcon = (type: JobTimelineEventType) => {
    switch (type) {
      case JobTimelineEventType.JobAction:
      case JobTimelineEventType.JobAttachment:
        return <InvoiceSettingsIcon className="path-stroke-current h-[18px] w-[18px] shrink-0 text-tertiaryText" />;
      case JobTimelineEventType.Note:
      case JobTimelineEventType.JobNote:
        return <JobTimelineNoteIcon className="shrink-0" />;
      default:
        return <InvoiceSettingsIcon className="path-stroke-current h-[18px] w-[18px] shrink-0 text-tertiaryText" />;
    }
  };

  const isListItemActionsVisible = (noteId: number) => {
    return noteId === currentActionsPopoverId;
  };

  const onCloseListItemActionsPopover = (noteId: number) => {
    if (currentActionsPopoverId === noteId) {
      setCurrentActionsPopoverId(null);
    }
  };

  const onToggleListItemActions = (noteId: number) => {
    if (noteId === currentActionsPopoverId) {
      setCurrentActionsPopoverId(null);
    } else {
      setCurrentActionsPopoverId(noteId);
    }
  };

  // eslint-disable-next-line react/no-multi-comp
  const ListActionsAnchor: React.FC<{ isPanelOpen: boolean }> = ({ isPanelOpen }) => {
    return (
      <div id="jobs-timeline-notes-more-options">
        <VerticalListActionsIcon
          height={16}
          width={4}
          className={twMerge(
            'mx-2 shrink-0 cursor-pointer',
            classnames({
              'fill-current text-secondary': isPanelOpen
            })
          )}
        />
      </div>
    );
  };

  // eslint-disable-next-line react/no-multi-comp
  const ListItemActionsMenu: FC<{ timelineEvent: JobTimelineEvent }> = ({ timelineEvent }) => {
    return (
      <div className="space-y-3 text-sbase font-semibold text-primaryText">
        <div
          id={`${timelineId}-3dot-editnote`}
          role="listbox"
          onClick={e => {
            e.stopPropagation();
            onEditNoteClick(timelineEvent);
          }}
          className="flex cursor-pointer flex-row items-center gap-2.5">
          <EditIconBlue height={16} width={16} />
          <div>Edit note</div>
        </div>
        <div
          id={`${timelineId}-3dot-delete-note`}
          role="listbox"
          onClick={() => onDeleteNoteClick(timelineEvent)}
          className="flex cursor-pointer flex-row items-center gap-2.5">
          <BinIcon />
          <div>Delete note</div>
        </div>
      </div>
    );
  };

  const showPopoverMenu = (jobTimelineEvent: JobTimelineEvent) => {
    return (
      (jobTimelineEvent.type === JobTimelineEventType.Note || jobTimelineEvent.type === JobTimelineEventType.JobNote) &&
      jobTimelineEvent.merchantId === currentUser.id
    );
  };

  return (
    <div className={`customNormalScroll flex grow flex-col overflow-y-auto p-4 pb-5 ${timelineClassName}`}>
      {jobTimeline?.map(
        (jobTimelineEvent, index) =>
          (limit ? index < limit : true) && (
            <div key={`job-timeline-${index + 1}`} className="flex gap-x-2.5">
              <div className="flex flex-col items-center">
                {getTimelineIcon(jobTimelineEvent.type)}
                {(limit && limit < jobTimeline.length ? index !== limit - 1 : index !== jobTimeline.length - 1) && (
                  <div className="h-full w-px  bg-borderGray"></div>
                )}
              </div>
              <div className={`w-full ${(limit ? index !== limit - 1 : index !== jobTimeline.length - 1) && 'pb-6'}`}>
                <div className="flex w-full items-center justify-between">
                  <div className={`text-17px font-semibold text-primaryText ${titleClassName}`}>
                    {jobTimelineEvent.title}
                  </div>
                  {showPopoverMenu(jobTimelineEvent) && (
                    <CustomPopover
                      anchorComponentId="job-notes-anchor"
                      showArrow={true}
                      arrowOffset="right-3"
                      minWidth="min-w-[150px]"
                      highlightAnchor={false}
                      anchorComponent={ListActionsAnchor}
                      zIndex="z-[101]"
                      offset="-right-3"
                      margin="mt-1"
                      onClosePopover={() => onCloseListItemActionsPopover(jobTimelineEvent.noteId)}
                      show={isListItemActionsVisible(jobTimelineEvent.noteId)}
                      onTogglePopover={() => onToggleListItemActions(jobTimelineEvent.noteId)}>
                      {<ListItemActionsMenu timelineEvent={jobTimelineEvent} />}
                    </CustomPopover>
                  )}
                </div>
                {jobTimelineEvent.subTitle && (
                  <div
                    className={`mt-2.5 max-w-[260px] whitespace-pre-wrap break-words text-sbase font-medium 
                      text-primaryText ${subTitleClassName}`}>
                    {jobTimelineEvent.subTitle}
                  </div>
                )}
                {jobTimelineEvent.attachments?.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-4">
                    {jobTimelineEvent.attachments.map((attachment, attachmentIndex) => (
                      <div
                        key={`job-event-${index}-attachment-${attachmentIndex}`}
                        className="relative h-[60px] max-h-[60px] w-[60px]">
                        <img
                          src={attachment.urlLink}
                          height={60}
                          width={60}
                          className="h-[60px] max-h-[60px] w-[60px] rounded object-cover"
                        />
                        {hasImageViewer && (
                          <ExpandImageIcon
                            className="absolute top-1 right-[3px] cursor-pointer"
                            onClick={() => showPreview(attachmentIndex, jobTimelineEvent)}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {jobTimelineEvent.subText && (
                  <div className={`mt-2 text-sbase text-accentText ${subTextClassName}`}>
                    {jobTimelineEvent.subText}
                  </div>
                )}
              </div>
            </div>
          )
      )}
    </div>
  );
};

export default JobTimeline;
